import { useEffect, useState } from 'react';
import Notice from '../../components/Notice.jsx';
import { api } from '../../lib/api.js';
import { ConsentCopyLink } from '../../components/LifecycleNotice.jsx';
import PermissionConsentTable from '../../components/PermissionConsentTable.jsx';
import { ALL_PERMISSIONS, SOURCE_REQUIRED_PERMISSIONS } from '../../../../shared/permission-map.mjs';

// ENTRAID-350 "start small": sources added after onboarding need the admin to consent to the new permissions. The save
// updates the customer's Entra app manifest at once; until a Global Administrator approves, the record lists the
// permissions still missing (missingPermissions) and this page says so, with the consent link right here.

/** The missing permissions on the record that onGuard can request (names only, unknown names dropped), sorted. */
export function missingFor(customer) {
  const list = Array.isArray(customer?.missingPermissions) ? customer.missingPermissions : [];
  return [...new Set(list.filter((p) => ALL_PERMISSIONS.includes(p)))].sort();
}

/** The enabled sources that wait for one of the missing permissions. */
export function sourcesNeedingConsent(enabledIds, missing) {
  const gap = new Set(missing);
  if (gap.size === 0) return new Set();
  return new Set([...(enabledIds ?? [])].filter((id) => (SOURCE_REQUIRED_PERMISSIONS[id] ?? []).some((p) => gap.has(p))));
}

/** How often the page asks again while Microsoft applies a consent just given (the backend caches a check for 45 s). */
export const SETTLE_POLL_MS = 15_000;

/**
 * ENTRAID-350 (one consent for the whole delta): the time until which Microsoft is still applying a consent just given
 * (`permissionsSettleUntil`, written by the backend), as epoch ms, or null when none or already over.
 */
export function settlingUntil(customer, nowMs = Date.now()) {
  const t = Date.parse(customer?.permissionsSettleUntil ?? '');
  return Number.isFinite(t) && t > nowMs ? t : null;
}

/**
 * While Microsoft applies the consent: asks the backend to check again every SETTLE_POLL_MS (onRefresh reloads the record)
 * and checks once more when the window ends, so a real gap then shows the consent link. Returns whether it is still settling.
 */
function useSettling(customer, onRefresh) {
  const [, setTick] = useState(0);
  const until = settlingUntil(customer);
  const customerId = customer?.customerId;
  useEffect(() => {
    if (!until || !customerId) return undefined;
    const poll = setInterval(() => {
      api.verifyConsent(customerId).then(() => onRefresh?.()).catch(() => {});
    }, SETTLE_POLL_MS);
    // At the end one last check lets the backend close the consent attempt (a real gap then shows the link).
    const end = setTimeout(() => {
      setTick((n) => n + 1);
      api.verifyConsent(customerId).catch(() => {}).finally(() => onRefresh?.());
    }, Math.max(0, until - Date.now()) + 1000);
    return () => { clearInterval(poll); clearTimeout(end); };
  }, [until, customerId, onRefresh]);
  return until !== null;
}

/** The "Needs admin consent" notice: what is waiting, why, and the consent link. Nothing when nothing is missing. */
export default function ConsentNeeded({ customer, waitingSources = [], onRefresh }) {
  const missing = missingFor(customer);
  const settling = useSettling(customer, onRefresh);
  // Only a customer with its own Entra app re-consents here (a legacy customer is cut over first, by IPC Global).
  if (missing.length === 0 || !customer?.entraAppClientId) return null;
  if (settling) {
    // The admin already approved: no second consent link while Microsoft finishes applying the first one.
    return (
      <Notice tone="info" title="Microsoft is applying the consent" titleAs="h3" testId="consent-settling">
        <p className="text-ipc-ink">
          A Global Administrator approved the new permissions. Microsoft can take a few minutes to apply every one of them.
          You do not need to consent again: this page checks on its own and updates when they are in place.
        </p>
      </Notice>
    );
  }
  const n = waitingSources.length;
  return (
    <Notice
      tone="warn"
      title="Needs admin consent"
      titleAs="h3"
      testId="consent-needed"
      dataAttrs={{ 'data-missing-count': String(missing.length) }}
    >
      <p className="text-ipc-ink">
        {n > 0
          ? `${n} data source${n === 1 ? '' : 's'} (${waitingSources.map((s) => s.label).join(', ')}) will start collecting once a Global Administrator approves ${missing.length} new read-only permission${missing.length === 1 ? '' : 's'}.`
          : `A Global Administrator still needs to approve ${missing.length} read-only permission${missing.length === 1 ? '' : 's'}.`}
        {' '}Your Microsoft Entra app already lists them, so one consent covers all of them. Microsoft's consent page shows
        every permission of the app; the ones already granted stay as they are, and the ones below are added.
      </p>
      <details className="mt-2">
        <summary className="cursor-pointer text-sm font-semibold text-ipc-ink">What the consent adds</summary>
        <div className="mt-2">
          <PermissionConsentTable
            permissions={missing}
            granted={Array.isArray(customer.consentedPermissions) ? customer.consentedPermissions : []}
            caption={`This consent adds ${missing.length} permission${missing.length === 1 ? '' : 's'}.`}
          />
        </div>
      </details>
      <div className="mt-3">
        <ConsentCopyLink customer={customer} label="Get the admin consent link" />
      </div>
    </Notice>
  );
}
